import { z } from 'zod';
import { khoangNgayVn } from './ngay.js';
/**
 * **Kỳ số liệu của cả app** — một mã, một hàm giải mã, dùng chung cho web và
 * máy chủ.
 *
 * Trước đợt 23/10/2026 có **ba** hệ kỳ sống cạnh nhau: `PERIODS`
 * (`7d|28d|90d|365d|all`) cho Tổng quan / Kênh / Phân tích, `?ky=YYYY-MM` cho
 * các trang tiền, và `lib/kyTrang.ts` của riêng trang kênh (thêm năm và khoảng
 * tự chọn). Hệ quả: bấm "Tháng 9/2026" ở trang kênh rồi sang Tổng quan là mất
 * kỳ, còn trang Network thì không có ô chọn nào.
 *
 * Nay **một** mã kỳ cho mọi trang, tám dạng:
 *
 * | Mã | Loại | Khoảng | Kỳ trước |
 * |---|---|---|---|
 * | `7d` `28d` `90d` `365d` | `lan` | mốc dữ liệu lùi N−1 ngày → mốc | N ngày liền trước |
 * | `all` | `all` | ngày sớm nhất có số liệu → mốc | **không có** |
 * | `YYYY` | `nam` | 01/01 → 31/12, bó ở mốc | cả năm trước (năm đang chạy: cùng số ngày đầu năm) |
 * | `YYYY-MM` | `thang` | cả tháng, bó ở mốc | cả tháng trước (tháng chưa trọn: cùng số ngày đầu tháng) |
 * | `tu-YYYY-MM-DD_den-YYYY-MM-DD` | `tuy-chinh` | đúng hai ngày đó, bó ở mốc | khoảng liền trước **cùng số ngày** |
 *
 * Hai luật xuyên suốt:
 *
 * 1. **Ngày cuối là ngày có số liệu**, không phải hôm nay: YouTube Analytics
 *    trễ 2–3 ngày, neo hôm nay thì cuối biểu đồ luôn có mấy cột 0 và "28 ngày
 *    qua" của app ngắn hơn 28 ngày thật của Studio.
 * 2. **Kỳ trước cùng loại**: tháng 3 so với tháng 2 (28 hay 29 ngày), năm
 *    nhuận so với năm trước (366 so 365). So "31 ngày liền trước" với tháng 3
 *    là so lệch ba ngày sang tháng 1.
 * 3. **Kỳ chưa trọn thì so cùng số ngày**: tháng đang chạy mới có số liệu tới
 *    26/10 so với 01/09–26/09, không so với cả 30 ngày tháng 9 (xem
 *    `kyTruocCungSoNgay`).
 */
/* ------------------------------------------------------------------ */
/* Mã kỳ                                                               */
/* ------------------------------------------------------------------ */
/** Số ngày của các kỳ lăn. Thêm mục ở đây là ô chọn có thêm mục. */
export const MA_KY_LAN = { '7d': 7, '28d': 28, '90d': 90, '365d': 365 };
export const MA_KY_MAC_DINH = '28d';
export const LA_THANG = /^\d{4}-(0[1-9]|1[0-2])$/;
export const LA_NAM = /^(19|20)\d{2}$/;
const LA_NGAY = /^\d{4}-\d{2}-\d{2}$/;
const LA_TUY_CHINH = /^tu-(\d{4}-\d{2}-\d{2})_den-(\d{4}-\d{2}-\d{2})$/;
/** `?ky=` có phải một mã kỳ hợp lệ. Mã lạ thì chỗ gọi về mặc định. */
export function laMaKy(x) {
    if (x in MA_KY_LAN || x === 'all' || LA_THANG.test(x) || LA_NAM.test(x))
        return true;
    const m = LA_TUY_CHINH.exec(x);
    return m !== null && (m[1] ?? '') <= (m[2] ?? '');
}
/** Hai ngày → mã kỳ tuỳ chỉnh. */
export function maTuyChinh(from, to) {
    return `tu-${from}_den-${to}`;
}
/** Loại của một mã kỳ. Mã lạ trả `null`. */
export function loaiCuaMaKy(ma) {
    if (ma in MA_KY_LAN)
        return 'lan';
    if (ma === 'all')
        return 'all';
    if (LA_NAM.test(ma))
        return 'nam';
    if (LA_THANG.test(ma))
        return 'thang';
    return laMaKy(ma) ? 'tuy-chinh' : null;
}
/** Hai ngày của một mã tuỳ chỉnh, `null` nếu mã không phải dạng đó. */
export function ngayCuaMaTuyChinh(ma) {
    const m = LA_TUY_CHINH.exec(ma);
    return m ? { from: m[1] ?? '', to: m[2] ?? '' } : null;
}
export const maKySchema = z.string().refine(laMaKy, { message: 'Mã kỳ không hợp lệ' });
/**
 * Query chung của mọi endpoint nhận kỳ: `?ky=<mã>` **hoặc** `?from=&to=`.
 *
 * `ky` là đường chính — chỉ nó mới nói được "kỳ trước" là tháng trước hay 30
 * ngày trước. `from`/`to` giữ cho chỗ gọi đã có sẵn hai ngày (và cho mọi
 * endpoint cũ chỉ nhận khoảng); lúc đó kỳ là `tuy-chinh`.
 */
export const kyQuerySchema = z
    .object({
    ky: maKySchema.optional(),
    from: z.string().regex(LA_NGAY).optional(),
    to: z.string().regex(LA_NGAY).optional(),
})
    .refine((q) => (q.from === undefined) === (q.to === undefined), {
    message: 'Khoảng ngày phải có cả from và to',
})
    .refine((q) => q.from === undefined || q.to === undefined || q.from <= q.to, {
    message: 'Ngày bắt đầu phải trước ngày kết thúc',
});
/* ------------------------------------------------------------------ */
/* Tiện ích ngày (thuần chuỗi, không phụ thuộc múi giờ)                */
/* ------------------------------------------------------------------ */
function khoa(d) {
    return d.toISOString().slice(0, 10);
}
function ngay(iso) {
    return new Date(`${iso}T00:00:00.000Z`);
}
/** Lùi `soNgay` ngày từ một khoá ngày `YYYY-MM-DD`. */
export function luiNgay(iso, soNgay) {
    const d = ngay(iso);
    d.setUTCDate(d.getUTCDate() - soNgay);
    return khoa(d);
}
/** Tiến `soNgay` ngày từ một khoá ngày `YYYY-MM-DD`. */
export function tienNgay(iso, soNgay) {
    const d = ngay(iso);
    d.setUTCDate(d.getUTCDate() + soNgay);
    return khoa(d);
}
/** Số ngày của một khoảng, tính cả hai đầu. */
export function soNgayKhoang(from, to) {
    return (Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1);
}
/** Ngày cuối tháng của kỳ `YYYY-MM`. */
export function cuoiThang(ky) {
    const [nam, thang] = ky.split('-').map(Number);
    return khoa(new Date(Date.UTC(nam ?? 1970, thang ?? 1, 0)));
}
/** `2026-10` lùi `n` tháng. */
export function thangLui(ky, n) {
    const [nam, thang] = ky.split('-').map(Number);
    const d = new Date(Date.UTC(nam ?? 1970, (thang ?? 1) - 1 - n, 1));
    return `${String(d.getUTCFullYear())}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}
/**
 * Khoảng này có trùng **trọn một tháng** không — `2026-09` hay `null`.
 *
 * Ranh giới giữa "kỳ tiền" và "kỳ tạm tính": trùng trọn tháng thì máy chủ đi
 * lại đúng đường của kỳ tháng (kể cả ảnh chụp lúc chốt) nên số khớp đối soát.
 */
export function thangTron(from, to) {
    if (!LA_NGAY.test(from) || !LA_NGAY.test(to))
        return null;
    const period = from.slice(0, 7);
    if (from !== `${period}-01` || to !== cuoiThang(period))
        return null;
    return period;
}
/**
 * Ngày cuối của **kỳ trước** cho một kỳ tháng / năm.
 *
 * Kỳ **đã trọn** (tháng hết tháng, năm hết năm) so với **cả** kỳ trước: tháng 3
 * so cả tháng 2, 28 ngày hay 29 ngày cũng vậy — đó là hai kỳ tiền hoàn chỉnh.
 *
 * Kỳ **chưa trọn** thì không: tháng 10 mới có số liệu tới 26/10 mà đem so với
 * cả 30 ngày tháng 9 là so 26 ngày với 30 ngày, ra "▼13,8%" trong khi kênh
 * không giảm (soát tay 29/10/2026 trên Tổng quan). Kỳ trước khi đó lấy **cùng
 * số ngày** tính từ đầu kỳ trước: 01/10–26/10 so với 01/09–26/09.
 *
 * Kẹp ở ngày cuối của kỳ trước vì kỳ trước có thể **ngắn hơn**: 01/03–30/03
 * (30 ngày) chỉ so được tới 28/02, không có 30/02.
 */
function kyTruocCungSoNgay(ky, prevFrom, prevCuoi) {
    if (ky.tron || ky.to === '' || ky.to < ky.from)
        return prevCuoi;
    const sauCung = tienNgay(prevFrom, soNgayKhoang(ky.from, ky.to) - 1);
    return sauCung < prevCuoi ? sauCung : prevCuoi;
}
/** Bó ngày cuối kỳ về mốc dữ liệu; mốc rỗng thì giữ nguyên. */
function boTheoMoc(to, ngayCuoi) {
    return ngayCuoi !== '' && ngayCuoi < to ? ngayCuoi : to;
}
/**
 * Nhãn của một mã kỳ, **không** cần mốc dữ liệu.
 *
 * Kỳ tuỳ chỉnh lấy nhãn từ hai ngày trên mã; muốn nhãn theo khoảng **đã bó**
 * ở mốc dữ liệu thì đọc `KyDaGiai.nhan`.
 */
export function nhanMaKy(ma) {
    const soNgay = MA_KY_LAN[ma];
    if (soNgay !== undefined)
        return `${String(soNgay)} ngày qua`;
    if (ma === 'all')
        return 'Toàn thời gian';
    if (LA_NAM.test(ma))
        return ma;
    if (LA_THANG.test(ma)) {
        const [nam, thang] = ma.split('-');
        return `Tháng ${String(Number(thang))}/${nam ?? ''}`;
    }
    const tc = ngayCuaMaTuyChinh(ma);
    return tc ? khoangNgayVn(tc.from, tc.to) : ma;
}
/**
 * Mã kỳ + mốc dữ liệu → khoảng ngày, kỳ trước và nhãn.
 *
 * **Một** hàm cho cả web lẫn máy chủ: hai nơi tự suy khoảng là hai nơi lệch
 * nhau một ngày, và lệch một ngày trong tiền là một lần đối soát sai.
 */
export function giaiMaKy(ma, moc) {
    const maThat = laMaKy(ma) ? ma : MA_KY_MAC_DINH;
    const { ngayDau, ngayCuoi } = moc;
    const loai = loaiCuaMaKy(maThat) ?? 'lan';
    const dung = (phan) => {
        const { from, to } = phan;
        const sanSang = from !== '' && to !== '' && from <= to;
        return {
            ma: maThat,
            loai,
            from,
            to,
            prevFrom: phan.prevFrom,
            prevTo: phan.prevTo,
            days: sanSang ? soNgayKhoang(from, to) : 0,
            /* Kỳ tháng **là** kỳ tiền của chính nó, kể cả khi chưa biết mốc dữ
               liệu (lúc đó `to` còn rỗng). Kỳ khác lấy tháng chứa ngày cuối. */
            thang: phan.thang ?? (to === '' ? '' : to.slice(0, 7)),
            nhan: phan.nhan ?? nhanMaKy(maThat),
            nhanKhoang: sanSang ? khoangNgayVn(from, to) : '',
            nhanKyTruoc: khoangNgayVn(phan.prevFrom, phan.prevTo),
            coKyTruoc: phan.coKyTruoc && phan.prevFrom !== '',
            tamTinh: phan.tamTinh,
            /* Kỳ tháng **luôn** là kỳ tiền, kể cả khi ngày cuối bị bó về mốc dữ
               liệu: nó vẫn là kỳ của sao kê. Kỳ khác chỉ thoát "tạm tính" khi trùng
               đúng trọn một tháng. */
            theoKhoang: loai !== 'thang' && thangTron(from, to) === null,
            sanSang,
        };
    };
    /* Kỳ lăn: N ngày tính tới mốc; kỳ trước là N ngày liền trước. */
    const soNgay = MA_KY_LAN[maThat];
    if (soNgay !== undefined) {
        if (ngayCuoi === '') {
            return dung({ from: '', to: '', prevFrom: '', prevTo: '', coKyTruoc: true, tamTinh: false });
        }
        const from = luiNgay(ngayCuoi, soNgay - 1);
        return dung({
            from,
            to: ngayCuoi,
            prevFrom: luiNgay(from, soNgay),
            prevTo: luiNgay(from, 1),
            coKyTruoc: true,
            /* Kỳ lăn kết thúc ở ngày **đã** có số liệu nên không có phần còn chạy. */
            tamTinh: false,
        });
    }
    /*
     * "Toàn thời gian": từ ngày sớm nhất có số liệu. **Không có kỳ trước** —
     * trước ngày đầu tiên có số liệu thì không có gì để so, và nhân đôi khoảng
     * là quét thêm vài chục năm để rồi bỏ đi.
     */
    if (maThat === 'all') {
        return dung({
            from: ngayDau,
            to: ngayCuoi,
            prevFrom: '',
            prevTo: '',
            coKyTruoc: false,
            tamTinh: false,
        });
    }
    /*
     * Năm: 01/01 → 31/12 (bó ở mốc); kỳ trước là **cả** năm trước — trừ khi năm
     * này còn đang chạy, lúc đó chỉ lấy **cùng số ngày đầu** của năm trước (xem
     * `kyTruocCungSoNgay`).
     */
    if (LA_NAM.test(maThat)) {
        const nam = Number(maThat);
        const truoc = String(nam - 1);
        const from = `${maThat}-01-01`;
        const to = ngayCuoi === '' ? '' : boTheoMoc(`${maThat}-12-31`, ngayCuoi);
        return dung({
            from,
            to,
            prevFrom: `${truoc}-01-01`,
            prevTo: kyTruocCungSoNgay({ from, to, tron: to === '' || to >= `${maThat}-12-31` }, `${truoc}-01-01`, `${truoc}-12-31`),
            coKyTruoc: true,
            tamTinh: ngayCuoi !== '' && ngayCuoi < `${maThat}-12-31`,
        });
    }
    /*
     * Tháng: cả tháng (bó ở mốc); kỳ trước là **cả** tháng trước — trừ khi tháng
     * này chưa trọn, lúc đó chỉ lấy **cùng số ngày đầu** của tháng trước (xem
     * `kyTruocCungSoNgay`).
     */
    if (LA_THANG.test(maThat)) {
        const truoc = thangLui(maThat, 1);
        const from = `${maThat}-01`;
        const to = ngayCuoi === '' ? '' : boTheoMoc(cuoiThang(maThat), ngayCuoi);
        return dung({
            thang: maThat,
            from,
            to,
            prevFrom: `${truoc}-01`,
            prevTo: kyTruocCungSoNgay({ from, to, tron: to === '' || to >= cuoiThang(maThat) }, `${truoc}-01`, cuoiThang(truoc)),
            coKyTruoc: true,
            tamTinh: ngayCuoi !== '' && ngayCuoi < cuoiThang(maThat),
        });
    }
    /*
     * Tuỳ chỉnh: đúng hai ngày trên mã, bó ở mốc. Kỳ trước là khoảng liền trước
     * **cùng số ngày** — đó là cách so duy nhất không cần đoán ý người chọn.
     */
    const tc = ngayCuaMaTuyChinh(maThat) ?? { from: '', to: '' };
    const to = ngayCuoi === '' ? '' : boTheoMoc(tc.to, ngayCuoi);
    const songSong = to !== '' && tc.from <= to;
    const days = songSong ? soNgayKhoang(tc.from, to) : 0;
    return dung({
        from: tc.from,
        to,
        prevFrom: songSong ? luiNgay(tc.from, days) : '',
        prevTo: songSong ? luiNgay(tc.from, 1) : '',
        coKyTruoc: true,
        tamTinh: false,
        nhan: songSong ? khoangNgayVn(tc.from, to) : khoangNgayVn(tc.from, tc.to),
    });
}
/* ------------------------------------------------------------------ */
/* Khoảng ngày cho phép chia nhân sự                                   */
/* ------------------------------------------------------------------ */
/**
 * Khoảng ngày để **chia nhân sự** của một kỳ — một hàm cho mọi màn hình.
 *
 * Kỳ **tháng** luôn ra **trọn tháng**, không phải khoảng đã bó ở mốc dữ liệu:
 * `team.chiaTheoKenh` đi đường kỳ tháng khi và chỉ khi khoảng trùng trọn một
 * tháng, và chỉ đường đó mới đọc ảnh chụp lúc chốt, mới tính chi phí theo
 * tháng (`chiPhiTrongKy`), mới cộng lỗ mang sang và mới chia tỷ lệ theo mẫu số
 * `soNgayKy` của cả tháng. Truyền khoảng đã bó vào là đi nhánh "theo khoảng":
 * chi phí tháng bị chia theo ngày, lỗ mang sang bị bỏ và dòng tỷ lệ hiệu lực
 * giữa kỳ đổi mẫu số — ra một con số **khác** bản nháp đối soát.
 *
 * Lệch thật đã gặp (Tổng quan vs trang Doanh thu, kỳ tháng 10/2026 có số liệu
 * tới 26/10): chia nhân sự 281,35 so với 282,62 $, công ty giữ lại lệch đúng
 * 1,27 $ ngược chiều. Hai màn cùng nhãn "Chia nhân sự · Tháng 10/2026".
 *
 * Kỳ **không** phải tháng (kỳ lăn, năm, tuỳ chỉnh) dùng đúng khoảng đã bó: đó
 * là kỳ của nó, và nó vốn đã là kỳ tạm tính.
 */
export function khoangChiaCuaKy(ma, khoang) {
    if (!LA_THANG.test(ma))
        return { from: khoang.from, to: khoang.to };
    return { from: `${ma}-01`, to: cuoiThang(ma) };
}
/* ------------------------------------------------------------------ */
/* Độ chi tiết của biểu đồ                                             */
/* ------------------------------------------------------------------ */
/**
 * Độ chi tiết của biểu đồ theo độ dài kỳ — cùng nấc với YouTube Studio.
 *
 * | Số ngày | Vẽ theo |
 * |---|---|
 * | ≤ 90 | ngày |
 * | ≤ 730 (2 năm) | tuần |
 * | còn lại | tháng |
 *
 * Vẽ 365 cột ngày trên một biểu đồ rộng 600px là 365 vạch rộng 1,6px: không
 * đọc được cột nào, mà vẫn tải về đủ 365 điểm.
 */
export function doChiTietCuaKy(soNgay) {
    if (soNgay <= 90)
        return 'day';
    if (soNgay <= 730)
        return 'week';
    return 'month';
}
//# sourceMappingURL=ky.js.map